import { useEffect, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Crown, CreditCard, Calendar, Check, AlertCircle } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { sessionId } = useContext(EnlightenmentContext);
  const [status, setStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    fetch(`${API}/subscription/check/${sessionId}`)
      .then(r => r.json()).then(setStatus).catch(() => {});
  }, [open, sessionId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await fetch(`${API}/subscription/cancel/${sessionId}`, { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: 'success', text: data.message });
        // Refresh status
        const fresh = await fetch(`${API}/subscription/check/${sessionId}`).then(r => r.json());
        setStatus(fresh);
      } else {
        setMessage({ type: 'error', text: data.error || 'Could not cancel' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error — try again' });
    } finally {
      setCancelling(false);
      setCancelConfirm(false);
    }
  };

  if (!status) return null;

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return iso; }
  };

  const reason = status.reason;
  const planLabel = reason === 'lifetime' ? 'Lifetime Member' : reason === 'subscribed' ? 'Monthly ($5.79/mo)' : reason === 'trial' ? 'Free Trial' : 'Expired';
  const planColor = reason === 'lifetime' ? '#D4A574' : reason === 'subscribed' ? '#4F378B' : reason === 'trial' ? '#4CAF50' : '#8B7DA8';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[350] flex items-center justify-center px-4"
          style={{ background: 'rgba(20, 14, 32, 0.7)', backdropFilter: 'blur(8px)' }}
          onClick={onClose}
          data-testid="subscription-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-md w-full rounded-3xl p-6"
            style={{
              background: 'linear-gradient(135deg, #FFF 0%, #F8F4FF 100%)',
              boxShadow: '0 24px 80px rgba(79,55,139,0.4)'
            }}
          >
            <button onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-purple-50"
              style={{ color: '#8B7DA8' }}
              data-testid="close-subscription-modal" aria-label="Close">
              <X size={18} />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <CreditCard size={18} style={{ color: '#4F378B' }} />
              <h3 className="text-base font-bold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
                My Subscription
              </h3>
            </div>

            {/* Current plan card */}
            <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: '#F0E8F8', border: `1px solid ${planColor}40` }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {reason === 'lifetime' ? <Crown size={16} style={{ color: planColor }} /> : <Check size={16} style={{ color: planColor }} />}
                  <span className="text-sm font-bold" style={{ color: planColor, fontFamily: "'Fredoka', sans-serif" }} data-testid="plan-label">
                    {planLabel}
                  </span>
                </div>
                {status.cancelled && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDE8E8', color: '#C0392B' }}>
                    Cancelled
                  </span>
                )}
              </div>
              {reason !== 'lifetime' && reason !== 'expired' && (
                <div className="flex items-center gap-1.5 text-xs" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                  <Calendar size={12} />
                  {status.cancelled
                    ? <>Access until <strong>{formatDate(status.sub_end)}</strong></>
                    : <>{status.days_left} day{status.days_left !== 1 ? 's' : ''} remaining</>
                  }
                </div>
              )}
              {reason === 'lifetime' && (
                <div className="text-xs" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                  Papaya is yours forever. No recurring charges.
                </div>
              )}
            </div>

            {/* Upgrade to Lifetime — only for non-lifetime users */}
            {reason !== 'lifetime' && (
              <button
                onClick={() => { onClose(); navigate('/subscription'); }}
                className="w-full mb-2 py-3 rounded-full text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #D4A574 0%, #4F378B 100%)',
                  boxShadow: '0 6px 18px rgba(79,55,139,0.3)',
                  fontFamily: "'Fredoka', sans-serif"
                }}
                data-testid="upgrade-lifetime-btn"
              >
                <Crown size={15} /> Upgrade to Lifetime ($101.08)
              </button>
            )}

            {/* Cancel monthly — only visible while actively on monthly and not yet cancelled */}
            {reason === 'subscribed' && !status.cancelled && (
              !cancelConfirm ? (
                <button
                  onClick={() => setCancelConfirm(true)}
                  className="w-full py-2.5 rounded-full text-xs font-semibold border"
                  style={{ backgroundColor: 'transparent', color: '#C0392B', borderColor: '#F5B7B1', fontFamily: "'Nunito', sans-serif" }}
                  data-testid="cancel-subscription-btn"
                >
                  Cancel Monthly Subscription
                </button>
              ) : (
                <div className="rounded-2xl p-3" style={{ backgroundColor: '#FDE8E8', border: '1px solid #F5B7B1' }}>
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle size={14} style={{ color: '#C0392B', flexShrink: 0, marginTop: 2 }} />
                    <p className="text-xs" style={{ color: '#C0392B', fontFamily: "'Nunito', sans-serif" }}>
                      Access continues until <strong>{formatDate(status.sub_end)}</strong>. No further charges will occur. Continue?
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCancelConfirm(false)}
                      className="flex-1 py-1.5 rounded-full text-[11px] font-semibold"
                      style={{ backgroundColor: '#FFF', color: '#5C5470' }}
                      data-testid="cancel-subscription-abort"
                    >Keep Subscription</button>
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="flex-1 py-1.5 rounded-full text-[11px] font-semibold text-white disabled:opacity-60"
                      style={{ backgroundColor: '#C0392B' }}
                      data-testid="cancel-subscription-confirm"
                    >{cancelling ? 'Cancelling...' : 'Yes, Cancel'}</button>
                  </div>
                </div>
              )
            )}

            {/* Trial/expired CTAs */}
            {(reason === 'trial' || reason === 'expired') && (
              <button
                onClick={() => { onClose(); navigate('/subscription'); }}
                className="w-full py-3 rounded-full text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)', fontFamily: "'Fredoka', sans-serif" }}
                data-testid="subscribe-monthly-btn"
              >
                {reason === 'expired' ? 'Resume with a Subscription' : 'Choose a Plan'}
              </button>
            )}

            {/* Messages */}
            {message && (
              <div
                className="text-xs mt-3 text-center"
                style={{ color: message.type === 'success' ? '#2E7D32' : '#C0392B', fontFamily: "'Nunito', sans-serif" }}
                data-testid="subscription-message"
              >
                {message.text}
              </div>
            )}

            <div className="text-[10px] text-center mt-4" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>
              Monthly plan: cancel anytime, no lock-in. Lifetime: one-time purchase, Papaya forever.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubscriptionModal;
